import { pluginForBlock } from './usePlugins';

/**
 * Plugin blocks in article Markdown:
 *
 *   :::<name>
 *   { …JSON the plugin's editor wrote… }
 *   :::
 *
 * Only names of known plugins count; anything else stays text, as do fenced
 * code blocks and a block whose JSON does not parse.
 */

const OPEN = /^\s*:::\s*([a-z][a-z0-9-]*)\s*$/i;
const CLOSE = /^\s*:::\s*$/;
const FENCE = /^\s*(```|~~~)/;

/** Splits Markdown into { type: 'markdown', text } and { type: 'plugin', plugin, data }. */
export function splitPluginBlocks(markdown = '', plugins = []) {
  const lines = markdown.split(/\r?\n/);
  const segments = [];
  let buffer = [];
  let block = null;
  let inFence = false;

  const flushText = () => {
    if (buffer.length) {
      segments.push({ type: 'markdown', text: buffer.join('\n') });
      buffer = [];
    }
  };

  for (const line of lines) {
    if (!block && FENCE.test(line)) inFence = !inFence;

    const open = !inFence && !block ? OPEN.exec(line) : null;
    const plugin = open ? pluginForBlock(plugins, open[1]) : null;
    if (plugin) {
      block = { plugin, lines: [line] };
      continue;
    }

    if (block && CLOSE.test(line)) {
      let data;
      try {
        data = JSON.parse(block.lines.slice(1).join('\n'));
      } catch {
        data = undefined;
      }
      if (data !== undefined) {
        flushText();
        segments.push({ type: 'plugin', plugin: block.plugin, data });
      } else {
        buffer.push(...block.lines, line);
      }
      block = null;
      continue;
    }

    (block ? block.lines : buffer).push(line);
  }

  if (block) buffer.push(...block.lines);
  flushText();
  return segments;
}

/** A plugin's block as the article text stores it. */
export function pluginBlockText(plugin, data) {
  return `:::${plugin.block.name}\n${JSON.stringify(data, null, 1)}\n:::`;
}

/** The plugin and its data when the article is nothing but one of its blocks, or null. */
export function pluginArticle(markdown = '', plugins = []) {
  const segments = splitPluginBlocks(markdown.trim(), plugins);
  return segments.length === 1 && segments[0].type === 'plugin' ? segments[0] : null;
}
